/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable import/no-cycle */
/* eslint-disable no-nested-ternary */
import { DependentSubmit } from '@brands/Dashboard/MyProfile/Patient/Sections/Dependent/EditDependent';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { ICase } from '@brands/services/cases/types/ICase';
import { RegisterMediaPayload } from '@brands/services/communication/registersConversationMedia';
import { addOneReportMedia } from '@brands/services/oneReport/addOneReportMedia';
import { confirmOneReportMedia } from '@brands/services/oneReport/confirmOneReportMedia';
import { registerOneReportMedia } from '@brands/services/oneReport/registerOneReportMedia';
import { base64ListToFileList } from '@brands/Utils/base64ToFile';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { getFileMimeType } from '@brands/Utils/truncateString';
import { parsePhoneNumber } from 'libphonenumber-js';
import moment from 'moment';
import { MultiValue } from 'react-select';
import { InsuranceForm } from 'src/brands/dentistry-one/Dashboard/Insurance/utils/types';
import { UserProfile } from 'src/brands/dentistry-one/services/identity/types/UserProfile';

import { PaymentProfile } from '../../../../services/identity/types/PaymentProfile';
import {
  Address,
  BasicInfo,
  Insurance,
  Marker,
  Organization,
  UserRoleName,
} from '../../../../services/identity/types/UserProfile';
import { Option } from '../../../../Utils/selectOptions';
import { PatientFormFiveSubmit, PatientNotCoveredInsuranceSubmit } from '../../../PatientForms/PatientForm/utils/types';
import { MyProfileSubmit, TokenResult } from './types';

export interface UploadedFile {
  name: string;
  dataURL?: string;
  media_id?: string;
  mimetype?: string;
}

export const updateData = (data: BasicInfo): BasicInfo => {
  const updatedData = { ...data };

  if (data.date_of_birth) {
    updatedData.date_of_birth = moment(data.date_of_birth).format('YYYY-MM-DD');
  }

  return updatedData;
};

const THREE_MINUTES_IN_MS = 0.5 * 60 * 1000; // 0.5 minutes in milliseconds

export const isAlmostEqualWithTolerance = (date1: string, date2: string): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  const differenceInMs = Math.abs(d1.getTime() - d2.getTime());
  return differenceInMs <= THREE_MINUTES_IN_MS;
};

// Attaches images to a case's One Report. Failures are surfaced as a toast
// (unchanged), and the boolean lets callers that must react to a failed
// attach — e.g. the intake flows, which otherwise mark an empty case
// complete — tell success from failure. Callers that only fire-and-forget
// can keep ignoring the return value. `false` means at least one image did
// not attach; `true` means everything the caller could act on succeeded.
export const uploadMediaToCase = async (
  newCase: ICase,
  imagesValue: string | number | string[] | UploadedFile[] | undefined | boolean | Insurance,
  userRole?: string,
): Promise<boolean> => {
  if (!Array.isArray(imagesValue)) return true;

  // Some staff-scoped POST /cases responses (notably Care Advisor) return the case
  // without the embedded `one_report` resource that the admin endpoint returns.
  // Re-fetch the case so we can resolve the `one_report_id` before attaching media.
  let oneReportId = newCase?.one_report ? newCase.one_report.id : newCase?.one_report_id?.toString() || '';
  if (!oneReportId && newCase?.id) {
    try {
      const refreshedCase = await getOneCase(
        Number(newCase.id),
        {
          include_patient_care_advisor_conversation: false,
          include_patient_provider_conversation: false,
          include_provider_care_advisor_conversation: false,
        },
        userRole,
      );
      oneReportId = refreshedCase?.one_report?.id || refreshedCase?.one_report_id?.toString() || '';
    } catch (error: unknown) {
      displayErrorDetails(error);
      return false;
    }
  }
  if (!oneReportId) return false;

  const results = await Promise.all(
    imagesValue.map(async (raw): Promise<boolean> => {
      if (typeof raw === 'string') return true;
      const image = raw as UploadedFile;
      try {
        // Fast path: image was already uploaded to S3 at capture time.
        if (image.media_id) {
          await addOneReportMedia(oneReportId, { media_id: image.media_id, note: '' }, userRole);
          return true;
        }
        // Legacy path: dataURL only — upload now and attach. Mirror the role-scoped
        // routing used by `uploadIntakeImage` so Care Advisor / Collector callers hit
        // `/care_advisor/media/...` and `/collector/media/...` instead of the
        // unscoped admin route.
        if (image.dataURL) {
          const file = (await base64ListToFileList(image.dataURL)) as File | null;
          if (!file) return false;
          const mimeType = image.mimetype || getFileMimeType(image.name);
          const payload: RegisterMediaPayload = {
            files: [{ filename: image.name, mimetype: mimeType }],
          };
          const registeredMedia = await registerOneReportMedia(payload);
          const mediaId = registeredMedia.files[0]?.upload_id;
          const uploadUrl = registeredMedia.files[0]?.upload_url;
          if (!mediaId || !uploadUrl) return false;
          // fetch resolves on 4xx/5xx, so an S3 failure would otherwise be
          // confirmed and attached as if the bytes had landed.
          const putResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'content-type': mimeType },
          });
          if (!putResponse.ok) {
            displayErrorDetails(new Error(`Upload failed (${putResponse.status})`));
            return false;
          }
          await confirmOneReportMedia(mediaId);

          await addOneReportMedia(oneReportId, { media_id: mediaId, note: '' }, userRole);
          return true;
        }
        // An entry with neither a pre-uploaded id nor bytes to upload cannot
        // be attached — report it rather than counting it as a success.
        displayErrorDetails(new Error('Attachment has no file data.'));
        return false;
      } catch (error: unknown) {
        displayErrorDetails(error);
        return false;
      }
    }),
  );
  return results.every(Boolean);
};

export function convertDateFormat(inputDate: string): string {
  const date = new Date(inputDate);
  const isoDate = date?.toISOString();
  return isoDate;
}

export const areArraysEqual = (arr1: Organization[] | Marker[], arr2: MultiValue<Option>): boolean => {
  // Check the lengths
  if (arr1.length !== arr2.length) {
    return false;
  }

  // Create a set of arr1 ids
  const idSet = new Set(arr1.map((org) => org.id));

  // Check if all arr2.id values exist in arr1
  for (const option of arr2) {
    if (!idSet.has(option.id as string)) {
      return false;
    }
  }

  return true;
};

export const updateDataInsurance = (data: MyProfileSubmit | DependentSubmit | PatientFormFiveSubmit): Insurance => {
  switch (data.insurance_through) {
    case 'none':
      return {
        insured_by: data.insurance_through,
      };
    case 'myself':
      return {
        insured_by: data.insurance_through,
        insurance_membership: {
          member_id: data.member_id,
          insurance_carrier_id: data.insurance_carrier_id,
          phone_number: parsePhoneNumber(data.iPhoneNumber, 'US')?.format('E.164') as string,
          group_number: data.group_number,
          address1: (data as PatientFormFiveSubmit).insurance_address || '',
          city: (data as PatientFormFiveSubmit).insurance_city || '',
          state: (data as PatientFormFiveSubmit).insurance_state || '',
          zip_code: (data as PatientFormFiveSubmit).insurance_zip_code || '',
        },
        state_funded_coverage_status: (data as PatientFormFiveSubmit).state_funded_coverage_status || '',
        is_secondary: (data as PatientFormFiveSubmit).is_secondary || false,
      };
    case '':
      return {
        insured_by: '',
        insurance_membership: undefined,
        insurance_through: undefined,
      };
    default:
      return {
        insured_by: data.insurance_through,
        insurance_membership: {
          member_id: data.member_id,
          phone_number: parsePhoneNumber(data.iPhoneNumber, 'US')?.format('E.164') as string,
          insurance_carrier_id: data.insurance_carrier_id,
          group_number: data.group_number,
          address1: (data as PatientFormFiveSubmit).insurance_address || '',
          city: (data as PatientFormFiveSubmit).insurance_city || '',
          state: (data as PatientFormFiveSubmit).insurance_state || '',
          zip_code: (data as PatientFormFiveSubmit).insurance_zip_code || '',
        },
        state_funded_coverage_status: (data as PatientFormFiveSubmit).state_funded_coverage_status || '',
        is_secondary: (data as PatientFormFiveSubmit).is_secondary || false,
        insurance_through: {
          first_name: data.iFirstName,
          middle_name: data.iMiddleName,
          date_of_birth: data.iDateOfBirth ? moment(data.iDateOfBirth).format('YYYY-MM-DD') : '0000-00-00',
          gender_identity: data.iGenderIdentity,
          last_name: data.iLastName,
          address: {
            address1: data.iAddress1,
            address2: data.iAddress2,
            city: data.iCity,
            state: data.iState,
            zip_code: data.iZipCode,
            country: data.country || 'United States of America',
          },
        },
      };
  }
};

export const updateDataInsuranceNewDesign = (data: InsuranceForm | PatientFormFiveSubmit): Insurance => {
  switch (data.insurance_through) {
    case 'none':
      return {
        insured_by: data.insurance_through,
      };
    case 'myself':
      return {
        insured_by: data.insurance_through,
        insurance_membership: {
          member_id: data.member_id,
          insurance_carrier_id: data.insurance_carrier_id,
          phone_number: parsePhoneNumber(data.insurance_phone_number, 'US')?.format('E.164') as string,
          group_number: data.group_number,
          address1: data.insurance_address || '',
          city: data.insurance_city || '',
          state: data.insurance_state || '',
          zip_code: data.insurance_zip_code || '',
        },
        state_funded_coverage_status: data.state_funded_coverage_status || '',
        is_secondary: data.is_secondary || false,
      };
    case '':
      return {
        insured_by: '',
        insurance_membership: undefined,
        insurance_through: undefined,
      };
    default:
      return {
        insured_by: data.insurance_through,
        insurance_membership: {
          member_id: data.member_id,
          phone_number: parsePhoneNumber(data.insurance_phone_number, 'US')?.format('E.164') as string,
          insurance_carrier_id: data.insurance_carrier_id,
          group_number: data.group_number,
          address1: data.insurance_address || '',
          city: data.insurance_city || '',
          state: data.insurance_state || '',
          zip_code: data.insurance_zip_code || '',
        },
        state_funded_coverage_status: data.state_funded_coverage_status || '',
        is_secondary: data.is_secondary || false,
        insurance_through: {
          first_name: 'insurance_first_name' in data ? data.insurance_first_name : data.iFirstName,
          date_of_birth:
            'insurance_date_of_birth' in data
              ? data.insurance_date_of_birth
                ? moment(data.insurance_date_of_birth).format('YYYY-MM-DD')
                : '0000-00-00'
              : data.iDateOfBirth
                ? moment(data.iDateOfBirth).format('YYYY-MM-DD')
                : '0000-00-00',
          gender_identity: 'insurance_gender' in data ? data.insurance_gender : data.iGenderIdentity,
          last_name: 'insurance_last_name' in data ? data.insurance_last_name : data.iLastName,
          address:
            'insurance_address1' in data
              ? {
                  address1: data.insurance_address1,
                  address2: data.insurance_address2,
                  city: data.insurance_city1,
                  state: data.insurance_state1,
                  zip_code: data.insurance_zip_code1,
                }
              : {
                  address1: data.iAddress1,
                  address2: data.iAddress2,
                  city: data.iCity,
                  state: data.iState,
                  zip_code: data.iZipCode,
                },
        },
      };
  }
};

export const convertInsuranceToInsuranceForm = (insurance: Insurance, user: UserProfile): InsuranceForm => {
  const insuranceThrough = insurance.insurance_through;
  return {
    member_id: insurance.insurance_membership?.member_id || '',
    group_number: insurance.insurance_membership?.group_number || '',
    insurance_through: insurance.insured_by || '',
    insurance_carrier_id: insurance.insurance_membership?.insurance_carrier_id,
    insurance_phone_number: insurance.insurance_membership?.phone_number || '',

    insurance_address: insurance.insurance_membership?.address?.address1 || '',
    insurance_city: insurance.insurance_membership?.address?.city || '',
    insurance_state: insurance.insurance_membership?.address?.state || '',
    insurance_zip_code: insurance.insurance_membership?.address?.zip_code || '',

    insurance_first_name: insuranceThrough?.first_name || user.basic_info.first_name || '',
    insurance_date_of_birth: insuranceThrough?.date_of_birth || user.basic_info.date_of_birth || '',
    insurance_gender: insuranceThrough?.gender_identity || user.basic_info.gender || '',
    insurance_last_name: insuranceThrough?.last_name || user.basic_info.last_name || '',

    insurance_address1: insuranceThrough?.address?.address1 || '',
    insurance_address2: insuranceThrough?.address?.address2 || '',
    insurance_zip_code1: insuranceThrough?.address?.zip_code || '',
    insurance_state1: insuranceThrough?.address?.state || '',
    insurance_city1: insuranceThrough?.address?.city || '',

    front_of_card: [] as UploadedFile[],
    back_of_card: [] as UploadedFile[],

    media: insurance.media || [],
    state_funded_coverage_status: insurance.state_funded_coverage_status || '',
    is_secondary: insurance.is_secondary || false,
  } as InsuranceForm;
};

export const updateDataSecondaryInsurance = (data: MyProfileSubmit | DependentSubmit): Insurance => {
  switch (data.secondary_insurance_through) {
    case 'none':
      return {
        insured_by: data.secondary_insurance_through,
      };
    case 'myself':
      return {
        insured_by: data.secondary_insurance_through,
        insurance_membership: {
          member_id: data.secondary_member_id,
          insurance_carrier_id: data.secondary_insurance_carrier_id,
          phone_number: parsePhoneNumber(data.secondary_iPhoneNumber, 'US')?.format('E.164') as string,
          group_number: data.secondary_group_number,
          address1: (data as PatientFormFiveSubmit).secondary_insurance_address || '',
          city: (data as PatientFormFiveSubmit).secondary_insurance_city || '',
          state: (data as PatientFormFiveSubmit).secondary_insurance_state || '',
          zip_code: (data as PatientFormFiveSubmit).secondary_insurance_zip_code || '',
        },
        state_funded_coverage_status: (data as PatientFormFiveSubmit).state_funded_coverage_status || '',
        is_secondary: true,
      };
    case '':
      return {
        insured_by: '',
        insurance_membership: undefined,
        insurance_through: undefined,
      };
    default:
      return {
        insured_by: data.secondary_insurance_through,
        insurance_membership: {
          member_id: data.secondary_member_id,
          phone_number: parsePhoneNumber(data.secondary_iPhoneNumber, 'US')?.format('E.164') as string,
          insurance_carrier_id: data.secondary_insurance_carrier_id,
          group_number: data.secondary_group_number,
          address1: (data as PatientFormFiveSubmit).secondary_insurance_address || '',
          city: (data as PatientFormFiveSubmit).secondary_insurance_city || '',
          state: (data as PatientFormFiveSubmit).secondary_insurance_state || '',
          zip_code: (data as PatientFormFiveSubmit).secondary_insurance_zip_code || '',
        },
        state_funded_coverage_status: (data as PatientFormFiveSubmit).state_funded_coverage_status || '',
        is_secondary: true,
        insurance_through: {
          first_name: data.secondary_iFirstName,
          middle_name: data.secondary_iMiddleName,
          date_of_birth: data.secondary_iDateOfBirth
            ? moment(data.secondary_iDateOfBirth).format('YYYY-MM-DD')
            : '0000-00-00',
          gender_identity: data.secondary_iGenderIdentity,
          last_name: data.secondary_iLastName,
          address: {
            address1: data.secondary_iAddress1,
            address2: data.secondary_iAddress2,
            city: data.secondary_iCity,
            state: data.secondary_iState,
            zip_code: data.secondary_iZipCode,
          },
        },
      };
  }
};

export const updateDataSecondaryInsuranceNewDesign = (data: InsuranceForm | PatientFormFiveSubmit): Insurance => {
  switch (data.secondary_insurance_through) {
    case 'none':
      return {
        insured_by: data.secondary_insurance_through,
      };
    case 'myself':
      return {
        insured_by: data.secondary_insurance_through,
        insurance_membership: {
          member_id: data.secondary_member_id,
          insurance_carrier_id: data.secondary_insurance_carrier_id,
          phone_number: parsePhoneNumber(data.secondary_insurance_phone_number, 'US')?.format('E.164') as string,
          group_number: data.secondary_group_number,
          address1: data.secondary_insurance_address || '',
          city: data.secondary_insurance_city || '',
          state: data.secondary_insurance_state || '',
          zip_code: data.secondary_insurance_zip_code || '',
        },
        state_funded_coverage_status: data.state_funded_coverage_status || '',
        is_secondary: true,
      };
    case '':
      return {
        insured_by: '',
        insurance_membership: undefined,
        insurance_through: undefined,
      };
    default:
      return {
        insured_by: data.secondary_insurance_through,
        insurance_membership: {
          member_id: data.secondary_member_id,
          phone_number: parsePhoneNumber(data.secondary_insurance_phone_number, 'US')?.format('E.164') as string,
          insurance_carrier_id: data.secondary_insurance_carrier_id,
          group_number: data.secondary_group_number,
          address1: data.secondary_insurance_address || '',
          city: data.secondary_insurance_city || '',
          state: data.secondary_insurance_state || '',
          zip_code: data.secondary_insurance_zip_code || '',
        },
        state_funded_coverage_status: data.state_funded_coverage_status || '',
        is_secondary: true,
        insurance_through: {
          first_name:
            'secondary_insurance_first_name' in data ? data.secondary_insurance_first_name : data.secondary_iFirstName,
          date_of_birth:
            'secondary_insurance_date_of_birth' in data
              ? data.secondary_insurance_date_of_birth
                ? moment(data.secondary_insurance_date_of_birth).format('YYYY-MM-DD')
                : '0000-00-00'
              : data.secondary_iDateOfBirth
                ? moment(data.secondary_iDateOfBirth).format('YYYY-MM-DD')
                : '0000-00-00',
          gender_identity:
            'secondary_insurance_gender' in data ? data.secondary_insurance_gender : data.secondary_iGenderIdentity,
          last_name:
            'secondary_insurance_last_name' in data ? data.secondary_insurance_last_name : data.secondary_iLastName,
          address:
            'secondary_insurance_address1' in data
              ? {
                  address1: data.secondary_insurance_address1,
                  address2: data.secondary_insurance_address2,
                  city: data.secondary_insurance_city1,
                  state: data.secondary_insurance_state1,
                  zip_code: data.secondary_insurance_zip_code1,
                }
              : {
                  address1: data.secondary_iAddress1,
                  address2: data.secondary_iAddress2,
                  city: data.secondary_iCity,
                  state: data.secondary_iState,
                  zip_code: data.secondary_iZipCode,
                },
        },
      };
  }
};

type SquareCardElement = {
  tokenize: (...args: any[]) => Promise<any>;
};

// Square's card form only takes the first five characters of a US ZIP, so an
// address stored as ZIP+4 is the same postal code as the one the buyer typed.
const normalizePostalCode = (postalCode: string): string =>
  postalCode
    .replace(/[^0-9a-z]/gi, '')
    .toUpperCase()
    .slice(0, 5);

/**
 * The billing ZIP for a card, from whichever source the form is using.
 *
 * Shared by `updateDataPayment` and `tokenizeAndVerifyBillingPostalCode` so the
 * ZIP we check the nonce against and the ZIP we store as the card's billing
 * address are one value by construction, not two expressions that happen to
 * agree.
 *
 * `isSameAddress: true` means "use the credit-card address fields on the form",
 * not "same as the patient's address".
 */
export const resolveBillingPostalCode = (
  data: PatientFormFiveSubmit | PatientNotCoveredInsuranceSubmit,
  userInfo: any,
  isSameAddress: boolean,
  dependentAddress?: Address,
  selectedPatientId?: number,
): string | undefined =>
  isSameAddress
    ? data.creditCardZipCode
    : userInfo?.id === selectedPatientId
      ? userInfo?.address?.zip_code
      : dependentAddress?.zip_code;

/**
 * The buyer's ZIP in the card form disagrees with the billing ZIP we are about
 * to store. Distinct from a request failure: the buyer can fix it themselves,
 * so callers keep the form open instead of tearing it down.
 *
 * The message names both ZIPs so the buyer can see which one to change. Note
 * that callers toast it through `displayErrorDetails`, which unmasks for
 * FullStory, so both values land in session recordings even though the ZIP
 * inputs on these forms are `fs-exclude`d.
 */
export class BillingPostalCodeMismatchError extends Error {
  constructor(tokenized: string, expected: string) {
    super(
      `The ZIP code you entered with your card (${tokenized}) does not match your billing ZIP code (${expected}). `,
    );
    this.name = 'BillingPostalCodeMismatchError';
  }
}

/**
 * Tokenize, then hold the buyer to the billing ZIP we are about to store.
 *
 * Square bakes the ZIP typed into its own card form into the nonce, and
 * CreateCard rejects a billing address whose postal code differs from it,
 * reported as INVALID_CARD_DATA against `source_id`, which says nothing about
 * the address.
 *
 * We deliberately do NOT push the billing ZIP into the card form to force the
 * two to agree. On the flows that bill to the address on file the buyer never
 * sees that ZIP, so overwriting what they typed would resolve the conflict by
 * storing an address they were never shown and never agreed to. Name the ZIP we
 * need and let them correct it.
 *
 * Square reports the tokenized ZIP as `details.billing.postalCode` (confirmed
 * against the sandbox); on the off chance it is ever absent there is nothing to
 * compare and the request proceeds as it did before.
 */
export const tokenizeAndVerifyBillingPostalCode = async (
  card: SquareCardElement,
  billingPostalCode?: string,
): Promise<TokenResult> => {
  const tokenResult = (await card.tokenize()) as TokenResult;
  const expected = (billingPostalCode ?? '').trim();
  if (!expected || tokenResult.status !== 'OK') return tokenResult;

  const tokenized = String(tokenResult.details?.billing?.postalCode ?? '').trim();
  if (tokenized && normalizePostalCode(tokenized) !== normalizePostalCode(expected)) {
    throw new BillingPostalCodeMismatchError(tokenized, expected);
  }
  return tokenResult;
};

export const updateDataPayment = (
  data: PatientFormFiveSubmit | PatientNotCoveredInsuranceSubmit,
  tokenResult: TokenResult,
  userInfo: any,
  isSameAddress: boolean,
  dependentAddress: Address,
  selectedPatientId: number,
  patientId?: number,
): PaymentProfile => {
  return {
    ...(userInfo.role.name === UserRoleName.Admin ||
    userInfo.role.name === UserRoleName.SuperAdmin ||
    userInfo.role.name === UserRoleName.OrganizationAdmin ||
    userInfo.role.name === UserRoleName.Collector ||
    userInfo.role.name === UserRoleName.CareAdvisor
      ? { user_id: patientId }
      : {}),
    verification_token: tokenResult.token,
    type: 'Card',
    is_default: true,
    credit_card: {
      brand: tokenResult.details?.card.brand,
      exp_month: tokenResult.details?.card.expMonth,
      exp_year: tokenResult.details?.card.expYear,
      last_four: tokenResult.details?.card.last4,
    },
    billing_contact: {
      address1: isSameAddress
        ? data.creditCardAddress1
        : userInfo.id === selectedPatientId
          ? userInfo.address?.address1
          : dependentAddress?.address1,
      address2: isSameAddress
        ? data.creditCardAddress2
        : userInfo.id === selectedPatientId
          ? userInfo.address?.address2
          : dependentAddress?.address2,
      city: isSameAddress
        ? data.creditCardCity
        : userInfo.id === selectedPatientId
          ? userInfo.address?.city
          : dependentAddress?.city,
      state: isSameAddress
        ? data.creditCardState
        : userInfo.id === selectedPatientId
          ? userInfo.address?.state
          : dependentAddress?.state,
      // No address on file to bill to (a dependent whose address has not loaded,
      // a profile with no ZIP): store the ZIP the buyer typed into the card form
      // rather than nothing, so the billing address cannot contradict the nonce
      // and CreateCard cannot fail with an opaque INVALID_CARD_DATA.
      postal_code:
        resolveBillingPostalCode(data, userInfo, isSameAddress, dependentAddress, selectedPatientId) ||
        tokenResult.details?.billing?.postalCode,
      family_name: '',
      given_name: '',
      email: userInfo.email,
      phone: userInfo.phone_number,
    },
  };
};
